import { useState } from "react";
import { Link } from "react-router";
import dayjs from "dayjs";
import { Loader2 } from "lucide-react";
import { authClient } from "@/lib/auth-client";
import { formatMoney, formatMoneyCompact } from "@/lib/format";
import { useSuretyPanel } from "@/hooks/queries/useSuretyPanel";
import { useSuretyMarket } from "@/hooks/queries/useSuretyMarket";
import { PROSPECT_TABS } from "@/components/surety/prospectTabs";
import {
  BarRow,
  EmptyNote,
  KpiCard,
  MonthSelect,
  PageHeader,
  SectionCard,
} from "@/components/surety/SuretyUI";
import { MonthlyBars } from "@/components/surety/MonthlyBars";
import { PendingGuaranteesTable } from "@/components/surety/PendingGuaranteesTable";
import { GuaranteeTable } from "@/components/surety/GuaranteeTable";
import { SecondaryLink } from "@/components/surety/SuretyActions";

/** Comparación contra el mismo tramo del mes anterior, como texto corto. */
function delta(current: number, previous: number) {
  if (previous === 0) return current > 0 ? { text: "nuevo", positive: true } : null;
  const change = (current - previous) / previous;
  if (Math.abs(change) >= 1) {
    return { text: `${change > 0 ? "▲" : "▼"} ${(current / previous).toFixed(1)}×`, positive: change > 0 };
  }
  return { text: `${change >= 0 ? "▲" : "▼"} ${Math.round(Math.abs(change) * 100)}%`, positive: change >= 0 };
}

/** El panel de la cartera: cómo le va a los clientes y qué garantías se vienen. */
export default function SuretyPanel() {
  const [month, setMonth] = useState<string | undefined>();
  const { data, isLoading, isError } = useSuretyPanel(month);
  const { data: market } = useSuretyMarket();
  const { data: activeOrg } = authClient.useActiveOrganization();

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-24">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }
  if (isError || !data) {
    return <div className="mx-auto w-full max-w-3xl px-4 py-24"><EmptyNote>No se pudo cargar el panel.</EmptyNote></div>;
  }

  const { kpis } = data;
  const maxRegion = Math.max(...data.regions.map((r) => r.amount), 1);

  return (
    <div className="mx-auto w-full flex-1 px-4 py-6 md:px-14">
      <PageHeader
        eyebrow={activeOrg?.name}
        title="Panel"
        accent={<MonthSelect value={data.month} onChange={setMonth} className="text-primary" />}
        subtitle={`Cómo le va a tu cartera de ${data.clientCount} empresas, qué garantías se vienen y qué cambió. Se actualiza cada mañana.`}
      />

      {data.clientCount === 0 ? (
        <SectionCard title="Tu cartera está vacía" subtitle="Carga los RUC de tus clientes para empezar a ver sus señales.">
          <SecondaryLink to="/surety/clients" className="self-start">Cargar clientes</SecondaryLink>
        </SectionCard>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <KpiCard
              label="Buenas pro de tu cartera"
              value={kpis.awards}
              delta={delta(kpis.awards, kpis.previousAwards)}
              hint={`${kpis.previousAwards} en los mismos ${data.elapsedDays} días del mes anterior`}
            />
            <KpiCard
              label="Monto adjudicado"
              value={formatMoneyCompact(kpis.amount)}
              delta={delta(kpis.amount, kpis.previousAmount)}
              hint={`${formatMoneyCompact(kpis.previousAmount)} en el mismo tramo del mes anterior`}
            />
            <KpiCard
              label="Garantías que van a necesitar"
              value={formatMoneyCompact(kpis.guaranteesNeeded)}
              valueClassName="text-primary"
              hint="Fiel cumplimiento estimado: 10% de esas buenas pro"
            />
            <KpiCard
              label="En juego en Mi equipo"
              value={formatMoneyCompact(kpis.teamInPlay)}
              to="/surety/team"
              hint={
                <>
                  {kpis.teamOpen} oportunidades abiertas
                  {kpis.teamUnassigned > 0 && (
                    <span className="font-bold text-red-600"> · {kpis.teamUnassigned} sin responsable</span>
                  )}
                </>
              }
            />
            <KpiCard
              label="Fugas del mes"
              value={kpis.leaks}
              valueClassName="text-orange-700"
              hint={`${formatMoneyCompact(kpis.leaksAmount)} en garantías de clientes que no emitiste tú`}
            />
            <KpiCard
              label="Fianzas vigentes de tus clientes"
              value={formatMoneyCompact(kpis.activeGuaranteesAmount)}
              valueClassName="text-amber-700"
              hint={
                kpis.nextExpiry
                  ? `${kpis.activeGuarantees} publicadas · la próxima vence el ${dayjs(kpis.nextExpiry).format("DD MMM YYYY")}`
                  : "Ninguna garantía publicada todavía"
              }
            />
          </div>

          <div className="grid gap-5 lg:grid-cols-3">
            <SectionCard
              className="lg:col-span-2"
              title="Buenas pro de tu cartera por mes"
              subtitle="Monto adjudicado y número de obras, sola o en consorcio"
            >
              <MonthlyBars months={data.months} selected={data.month} />
            </SectionCard>
            <SectionCard title="Dónde ganan tus clientes" subtitle="Monto adjudicado en los últimos 6 meses">
              {data.regions.length === 0 ? (
                <EmptyNote>Sin buenas pro en los últimos 6 meses.</EmptyNote>
              ) : (
                <div className="flex flex-col gap-1">
                  {data.regions.map((region) => (
                    <BarRow
                      key={region.region}
                      label={region.region}
                      value={formatMoneyCompact(region.amount)}
                      pct={(region.amount / maxRegion) * 100}
                      barClassName="bg-purple-500"
                    />
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          <div className="grid gap-5 lg:grid-cols-3">
            <SectionCard
              className="lg:col-span-2"
              title="Necesitan garantía ahora"
              subtitle="Clientes que ganaron en los últimos 30 días y todavía no firman contrato"
              action={<SecondaryLink to="/surety/team">Ver en Mi equipo</SecondaryLink>}
            >
              <PendingGuaranteesTable rows={data.pendingGuarantees} />
            </SectionCard>
            <div className="flex flex-col gap-5">
              <SectionCard title="Top clientes" subtitle="Monto adjudicado en los últimos 6 meses">
                {data.topClients.length === 0 ? (
                  <EmptyNote>Todavía sin buenas pro.</EmptyNote>
                ) : (
                  <ol className="flex flex-col gap-3">
                    {data.topClients.map((client) => (
                      <li key={client.companyId} className="flex items-start justify-between gap-3">
                        <Link to={`/companies/${client.companyId}`} className="cursor-pointer">
                          <span className="block font-bold text-slate-800 hover:text-primary dark:text-slate-100">
                            {client.name}
                          </span>
                          <span className="text-xs text-slate-500">
                            {client.awards} {client.awards === 1 ? "obra" : "obras"}
                            {client.department ? ` · ${client.department}` : ""}
                          </span>
                        </Link>
                        <span className="font-bold text-slate-900 dark:text-slate-50">
                          {formatMoneyCompact(client.amount)}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
                <SecondaryLink to="/surety/clients" className="self-start">
                  Ver los {data.clientCount} clientes
                </SecondaryLink>
              </SectionCard>
              <SectionCard title="Fugas del mes" subtitle="Garantías de tus clientes que emitió otro">
                {data.leaks.length === 0 ? (
                  <EmptyNote>Sin fugas este mes.</EmptyNote>
                ) : (
                  <ul className="flex flex-col gap-3">
                    {data.leaks.map((leak, index) => (
                      <li key={`${leak.contractingId}-${index}`} className="text-sm">
                        <span className="font-bold text-slate-800 dark:text-slate-100">
                          {leak.bondholder ?? leak.nomenclature}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {formatMoney(leak.amount)} · {leak.issuer ?? "emisor sin leer"}
                          {leak.broker ? ` · vía ${leak.broker}` : ""}
                          {leak.validUntil ? ` · vence ${dayjs(leak.validUntil).format("DD MMM YYYY")}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <SectionCard
              title="Vencimientos de tu cartera"
              subtitle="Fianzas publicadas de tus clientes, la emita quien la emita"
            >
              <GuaranteeTable
                guarantees={data.expirations}
                empty="Todavía no hay garantías publicadas de tus clientes."
              />
              <p className="text-xs text-slate-500">
                Solo ~30% de los contratos de obra publica su garantía en el SEACE.
              </p>
            </SectionCard>
            <SectionCard
              title="Para descubrir esta semana"
              subtitle="Empresas que todavía no son tus clientes"
              action={<SecondaryLink to="/surety/market">Ir a Mercado</SecondaryLink>}
            >
              {market ? (
                <>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    <span className="text-3xl font-extrabold text-amber-700">
                      {market.prospects.winners.filter((row) => !row.isClient).length}
                    </span>{" "}
                    ganaron este mes y todavía no son tus clientes
                  </p>
                  {/* Las mismas pestañas del Mercado, sin la de tus clientes. */}
                  <ul className="flex flex-col divide-y text-sm">
                    {PROSPECT_TABS.filter((item) => item.id !== "clients").map((item) => [
                      item.id === "winners" ? "Ganaron (sin tus clientes)" : item.label,
                      market.prospects.winners.filter(
                        (row) => !row.isClient && item.matches(row),
                      ).length,
                    ]).map(([label, count]) => (
                      <li key={label} className="flex justify-between py-2">
                        <span className="font-semibold text-slate-700 dark:text-slate-200">{label}</span>
                        <span className="font-bold">{count}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <Loader2 className="size-5 animate-spin text-primary" />
              )}
            </SectionCard>
          </div>
        </div>
      )}
    </div>
  );
}
